'use client';

import { useState, useEffect } from 'react';
import ProductCard from './ProductCard';
import FilterSidebar from './FilterSidebar';
import SearchBar from './SearchBar';
import QuickViewModal from './QuickViewModal';
import Icon from '@/components/ui/AppIcon';

interface Product {
  id: string;
  name: string;
  category: string;
  manufacturer: string;
  image: string;
  alt: string;
  compliance: string[];
  description: string;
  specifications: {
    label: string;
    value: string;
  }[];
  features: string[];
  inStock: boolean;
}

export default function ProductsInteractive() {
  const [isHydrated, setIsHydrated] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFilters, setSelectedFilters] = useState({
    categories: [] as string[],
    manufacturers: [] as string[],
    compliance: [] as string[]
  });
  const [sortBy, setSortBy] = useState('relevance');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [quickViewProduct, setQuickViewProduct] = useState<Product | null>(null);
  const [isQuickViewOpen, setIsQuickViewOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12;

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const mockProducts: Product[] = [
  {
    id: '1',
    name: 'Ziehm Vision C-Arm System',
    category: 'Imaging Equipment',
    manufacturer: 'Ziehm Imaging',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1fc1e014f-1765777377755.png",
    alt: 'Ziehm Vision C-arm fluoroscopy system with mobile base and adjustable positioning',
    compliance: ['FDA', 'CE', 'ISO 13485'],
    description: 'Advanced mobile C-arm system with high-resolution imaging capabilities for surgical and interventional procedures.',
    specifications: [
    { label: 'Image Resolution', value: '1920 x 1080 pixels' },
    { label: 'Rotation Range', value: '±120 degrees' },
    { label: 'Weight Capacity', value: '500 lbs' }],

    features: [
    'Real-time high-definition imaging',
    'Advanced dose management',
    'Touchscreen control interface'],

    inStock: true
  },
  {
    id: '2',
    name: 'Mobile C-Arm X-Ray System',
    category: 'Imaging Equipment',
    manufacturer: 'Medical Imaging Solutions',
    image: 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQ3NsX3XwiCBH92MenL--NXz9nWKTh-gpwCwg&s',
    alt: 'Compact mobile C-arm X-ray system with digital imaging display',
    compliance: ['FDA', 'CE'],
    description: 'Portable C-arm system designed for versatility in orthopedic and vascular procedures.',
    specifications: [
    { label: 'Image Resolution', value: '1024 x 1024 pixels' },
    { label: 'Mobility', value: 'Full 360-degree rotation' }],

    features: [
    'Compact design for tight spaces',
    'Digital image processing',
    'Easy maneuverability'],

    inStock: true
  },
  {
    id: '3',
    name: 'Genoray C-Arm System',
    category: 'Imaging Equipment',
    manufacturer: 'Genoray',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1a02964f4-1765170814071.png",
    alt: 'Genoray C-arm fluoroscopy system with advanced imaging technology',
    compliance: ['FDA', 'CE', 'ISO 13485'],
    description: 'High-performance C-arm system with exceptional image quality and advanced features.',
    specifications: [
    { label: 'Image Enhancement', value: 'AI-powered processing' },
    { label: 'Display', value: '21-inch high-resolution monitor' }],

    features: [
    'Superior image quality',
    'User-friendly interface',
    'Radiation dose reduction'],

    inStock: true
  },
  {
    id: '4',
    name: 'Surgical C-Arm Imaging System',
    category: 'Imaging Equipment',
    manufacturer: 'Maven Imaging',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1d1c619ec-1765777371304.png",
    alt: 'Surgical C-arm with compact design for operating room use',
    compliance: ['FDA', 'CE'],
    description: 'Compact surgical C-arm optimized for space-constrained operating rooms.',
    specifications: [
    { label: 'Footprint', value: 'Ultra-compact design' },
    { label: 'Rotation', value: 'Full orbital capability' }],

    features: [
    'Space-saving design',
    'Quick setup and positioning',
    'High-quality imaging'],

    inStock: true
  },
  {
    id: '5',
    name: 'Genoray Oscar 15 Mobile C-Arm',
    category: 'Imaging Equipment',
    manufacturer: 'Genoray',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1cb0883e9-1765170816017.png",
    alt: 'Genoray Oscar 15 mobile C-arm with ergonomic design and digital display',
    compliance: ['FDA', 'CE', 'ISO 13485'],
    description: 'Oscar 15 mobile C-arm featuring advanced imaging technology and ergonomic operation.',
    specifications: [
    { label: 'Imaging Modes', value: 'Fluoroscopy, Radiography' },
    { label: 'Power', value: '110-240V universal' }],

    features: [
    'Ergonomic control panel',
    'Digital image storage',
    'Dose management system'],

    inStock: true
  },
  {
    id: '6',
    name: 'Mini C-Arm Fluoroscopy System',
    category: 'Imaging Equipment',
    manufacturer: 'Orthopedic Imaging',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_109141408-1765777376680.png",
    alt: 'Compact mini C-arm system for extremity imaging procedures',
    compliance: ['FDA', 'CE'],
    description: 'Mini C-arm designed specifically for extremity imaging in orthopedic applications.',
    specifications: [
    { label: 'Target Area', value: 'Extremities and small anatomies' },
    { label: 'Portability', value: 'Lightweight mobile design' }],

    features: [
    'Specialized for orthopedics',
    'Compact footprint',
    'Easy positioning'],

    inStock: true
  },
  {
    id: '7',
    name: 'Fixed X-Ray Radiography System',
    category: 'Imaging Equipment',
    manufacturer: 'Radiology Systems Inc',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1a082ba1b-1765777369864.png",
    alt: 'Fixed X-ray machine with ceiling-mounted tube for general radiography',
    compliance: ['FDA', 'ISO 13485'],
    description: 'Fixed installation X-ray system for comprehensive diagnostic radiography.',
    specifications: [
    { label: 'Installation', value: 'Ceiling-mounted' },
    { label: 'Coverage', value: 'Full room radiography' }],

    features: [
    'High-capacity generator',
    'Automated exposure control',
    'Digital detector compatible'],

    inStock: true
  },
  {
    id: '8',
    name: 'Hospital Radiology C-Arm Device',
    category: 'Imaging Equipment',
    manufacturer: 'Medical Fluoroscopy Systems',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1882ef67f-1765777371811.png",
    alt: 'Hospital-grade C-arm fluoroscopy device with advanced imaging features',
    compliance: ['FDA', 'CE', 'ISO 13485'],
    description: 'Professional C-arm fluoroscopy device for hospital radiology departments.',
    specifications: [
    { label: 'Applications', value: 'Surgical and diagnostic procedures' },
    { label: 'Image Quality', value: 'High-definition digital imaging' }],

    features: [
    'Hospital-grade construction',
    'Advanced image processing',
    'Comprehensive safety features'],

    inStock: true
  },
  {
    id: '9',
    name: 'Advanced Fluoroscopy Workstation',
    category: 'Imaging Equipment',
    manufacturer: 'Diagnostic Imaging Corp',
    image: 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQbI5fJOEvw_eB8nioaHQPSXMnqk2WSRZZlTA&s',
    alt: 'Advanced fluoroscopy workstation with integrated digital imaging',
    compliance: ['FDA', 'CE'],
    description: 'State-of-the-art fluoroscopy workstation with comprehensive imaging capabilities.',
    specifications: [
    { label: 'Workstation Type', value: 'Integrated fluoroscopy suite' },
    { label: 'Connectivity', value: 'DICOM and PACS compatible' }],

    features: [
    'Integrated imaging suite',
    'Digital workflow optimization',
    'Advanced post-processing'],

    inStock: true
  },
  {
    id: '10',
    name: 'GE OEC 6800 Mini C-Arm',
    category: 'Imaging Equipment',
    manufacturer: 'GE Healthcare',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_184d7b4d7-1765777373414.png",
    alt: 'GE OEC 6800 mini C-arm system for orthopedic and extremity imaging',
    compliance: ['FDA', 'CE', 'ISO 13485'],
    description: 'GE OEC 6800 mini C-arm renowned for reliability and image quality in orthopedic procedures.',
    specifications: [
    { label: 'Manufacturer', value: 'GE Healthcare' },
    { label: 'Specialty', value: 'Orthopedic imaging' }],

    features: [
    'Industry-standard reliability',
    'Excellent image resolution',
    'Compact and maneuverable'],

    inStock: true
  },
  {
    id: '11',
    name: 'Siemens Artis Zee Biplane System',
    category: 'Imaging Equipment',
    manufacturer: 'Siemens Healthineers',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_115becfac-1765777372988.png",
    alt: 'Siemens Artis zee biplane angiography system with dual imaging planes',
    compliance: ['FDA', 'CE', 'ISO 13485'],
    description: 'Premium biplane angiography system for advanced interventional procedures.',
    specifications: [
    { label: 'Configuration', value: 'Biplane imaging system' },
    { label: 'Applications', value: 'Neuro and cardiac interventions' }],

    features: [
    'Simultaneous dual-plane imaging',
    'Advanced 3D reconstruction',
    'Dose management technology'],

    inStock: true
  },
  {
    id: '12',
    name: 'Infinix-i Biplane Angiography System',
    category: 'Imaging Equipment',
    manufacturer: 'Canon Medical Systems',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_13f683093-1765777372979.png",
    alt: 'Canon Infinix-i biplane angiography system for cardiovascular imaging',
    compliance: ['FDA', 'CE'],
    description: 'High-performance biplane system designed for cardiovascular and neurological interventions.',
    specifications: [
    { label: 'Imaging Type', value: 'Biplane angiography' },
    { label: 'Target Procedures', value: 'Cardiac and neuro' }],

    features: [
    'Dual-plane simultaneous imaging',
    'Advanced hemodynamic monitoring',
    'Low-dose imaging protocols'],

    inStock: true
  },
  {
    id: '13',
    name: 'Advanced CT Scanner System',
    category: 'Imaging Equipment',
    manufacturer: 'CT Imaging Solutions',
    image: '/assets/images/ct_scanner_diagnostic_system.png',
    alt: 'Modern CT scanner with circular gantry opening and patient positioning table',
    compliance: ['FDA', 'CE', 'ISO 13485'],
    description: 'State-of-the-art CT scanner for comprehensive diagnostic imaging and cross-sectional analysis.',
    specifications: [
      { label: 'Slices', value: '64-slice CT imaging' },
      { label: 'Gantry Opening', value: '70 cm wide bore' },
      { label: 'Scan Speed', value: 'Sub-second rotation' }
    ],
    features: [
      'High-resolution 3D imaging',
      'Low-dose radiation protocols',
      'Advanced reconstruction algorithms'
    ],
    inStock: true
  },
  {
    id: '14',
    name: 'Wide Bore MRI Scanner',
    category: 'Imaging Equipment',
    manufacturer: 'MRI Technologies Inc',
    image: '/assets/images/mri_scanner_wide_bore.png',
    alt: 'High-field MRI scanner with wide bore opening for patient comfort',
    compliance: ['FDA', 'CE', 'ISO 13485'],
    description: 'High-field MRI system with wide bore design for enhanced patient comfort and diagnostic capabilities.',
    specifications: [
      { label: 'Field Strength', value: '3 Tesla magnetic field' },
      { label: 'Bore Diameter', value: '70 cm wide bore' },
      { label: 'Imaging Speed', value: 'Fast parallel imaging' }
    ],
    features: [
      'Wide bore for patient comfort',
      'Advanced coil technology',
      'Quiet scanning sequences'
    ],
    inStock: true
  },
  {
    id: '15',
    name: 'Digital Ultrasound System',
    category: 'Imaging Equipment',
    manufacturer: 'Ultrasound Diagnostics Corp',
    image: '/assets/images/ultrasound_diagnostic_machine.png',
    alt: 'Digital ultrasound machine with touchscreen display and multiple transducer probes',
    compliance: ['FDA', 'CE'],
    description: 'Advanced ultrasound system with multiple transducers for comprehensive diagnostic imaging.',
    specifications: [
      { label: 'Display', value: '21.5-inch touchscreen' },
      { label: 'Transducers', value: 'Multi-frequency probes' },
      { label: 'Modes', value: 'B-Mode, M-Mode, Doppler' }
    ],
    features: [
      'Real-time image processing',
      'Portable cart design',
      'DICOM connectivity'
    ],
    inStock: true
  },
  {
    id: '16',
    name: 'Digital Mammography Unit',
    category: 'Imaging Equipment',
    manufacturer: 'Women\'s Health Imaging',
    image: '/assets/images/digital_mammography_unit.png',
    alt: 'Digital mammography machine with compression paddle for breast cancer screening',
    compliance: ['FDA', 'CE', 'ISO 13485'],
    description: 'State-of-the-art digital mammography system for early breast cancer detection and screening.',
    specifications: [
      { label: 'Technology', value: 'Digital flat panel detector' },
      { label: 'Resolution', value: 'High-resolution imaging' },
      { label: 'Compression', value: 'Automatic compression control' }
    ],
    features: [
      'Low-dose digital imaging',
      'Computer-aided detection',
      'Tomosynthesis capable'
    ],
    inStock: true
  },
  {
    id: '17',
    name: 'Portable X-Ray Unit',
    category: 'Imaging Equipment',
    manufacturer: 'Mobile Imaging Systems',
    image: '/assets/images/portable_xray_mobile_unit.png',
    alt: 'Portable mobile X-ray unit with battery power for bedside radiography',
    compliance: ['FDA', 'CE'],
    description: 'Battery-powered portable X-ray system for bedside imaging in hospitals and care facilities.',
    specifications: [
      { label: 'Power Source', value: 'Rechargeable battery' },
      { label: 'Mobility', value: 'Compact wheeled design' },
      { label: 'Tube Head', value: 'Adjustable positioning' }
    ],
    features: [
      'Wireless operation',
      'Lightweight and maneuverable',
      'Digital detector compatible'
    ],
    inStock: true
  },
  {
    id: '18',
    name: 'DEXA Bone Densitometry Scanner',
    category: 'Imaging Equipment',
    manufacturer: 'Bone Health Diagnostics',
    image: '/assets/images/bone_densitometry_dexa_scanner.png',
    alt: 'DEXA bone densitometry scanner for osteoporosis screening and diagnosis',
    compliance: ['FDA', 'CE'],
    description: 'Dual-energy X-ray absorptiometry system for accurate bone density measurement and osteoporosis assessment.',
    specifications: [
      { label: 'Technology', value: 'Dual-energy X-ray' },
      { label: 'Scan Areas', value: 'Spine, hip, whole body' },
      { label: 'Precision', value: 'High-precision BMD measurement' }
    ],
    features: [
      'Fast scan times',
      'Low radiation exposure',
      'Vertebral fracture assessment'
    ],
    inStock: true
  },
  {
    id: '19',
    name: 'Cardiac Catheterization Lab',
    category: 'Imaging Equipment',
    manufacturer: 'Cardiac Interventions Inc',
    image: '/assets/images/cardiac_catheterization_lab_system.png',
    alt: 'Cardiac catheterization laboratory with angiography and hemodynamic monitoring',
    compliance: ['FDA', 'CE', 'ISO 13485'],
    description: 'Complete cardiac catheterization laboratory system for interventional cardiology procedures.',
    specifications: [
      { label: 'Imaging', value: 'High-resolution angiography' },
      { label: 'Monitoring', value: 'Advanced hemodynamics' },
      { label: 'Table', value: 'Floating cardiac table' }
    ],
    features: [
      'Real-time fluoroscopy',
      'Integrated monitoring',
      'Advanced image processing'
    ],
    inStock: true
  },
  {
    id: '20',
    name: 'Digital Radiography Panel',
    category: 'Imaging Equipment',
    manufacturer: 'DR Panel Technologies',
    image: '/assets/images/digital_radiography_detector_panel.png',
    alt: 'Digital radiography DR detector panel with wireless connectivity',
    compliance: ['FDA', 'CE'],
    description: 'Wireless digital radiography detector panel for retrofitting existing X-ray systems.',
    specifications: [
      { label: 'Technology', value: 'Wireless flat panel detector' },
      { label: 'Size', value: '14" x 17" imaging area' },
      { label: 'Connectivity', value: 'Wi-Fi and wired options' }
    ],
    features: [
      'Portable and lightweight',
      'Instant image preview',
      'Battery-powered operation'
    ],
    inStock: true
  },
  {
    id: '21',
    name: 'Interventional Radiology Suite',
    category: 'Imaging Equipment',
    manufacturer: 'Advanced IR Systems',
    image: '/assets/images/ct_scanner_diagnostic_system.png',
    alt: 'Interventional radiology suite with advanced imaging and procedure capabilities',
    compliance: ['FDA', 'CE', 'ISO 13485'],
    description: 'Complete interventional radiology suite with advanced imaging for minimally invasive procedures.',
    specifications: [
      { label: 'Configuration', value: 'Fixed angiography system' },
      { label: 'Applications', value: 'Multi-specialty procedures' },
      { label: 'Table', value: 'Radiolucent procedure table' }
    ],
    features: [
      'High-resolution imaging',
      'Advanced roadmapping',
      'Integrated procedural tools'
    ],
    inStock: true
  },
  {
    id: '22',
    name: 'PET-CT Hybrid Scanner',
    category: 'Imaging Equipment',
    manufacturer: 'Nuclear Medicine Imaging',
    image: '/assets/images/mri_scanner_wide_bore.png',
    alt: 'PET-CT hybrid scanner for combined functional and anatomical imaging',
    compliance: ['FDA', 'CE', 'ISO 13485'],
    description: 'Integrated PET-CT scanner combining functional and anatomical imaging for oncology and research.',
    specifications: [
      { label: 'Modality', value: 'PET-CT hybrid system' },
      { label: 'CT Slices', value: '64-slice CT component' },
      { label: 'PET Detector', value: 'Time-of-flight technology' }
    ],
    features: [
      'Simultaneous acquisition',
      'Advanced image fusion',
      'Low-dose protocols'
    ],
    inStock: true
  },
  {
    id: '23',
    name: 'Surgical Navigation System',
    category: 'Imaging Equipment',
    manufacturer: 'Surgical Guidance Technologies',
    image: '/assets/images/ultrasound_diagnostic_machine.png',
    alt: 'Surgical navigation system with real-time imaging guidance',
    compliance: ['FDA', 'CE'],
    description: 'Advanced surgical navigation system providing real-time image guidance for precision procedures.',
    specifications: [
      { label: 'Technology', value: 'Optical tracking system' },
      { label: 'Accuracy', value: 'Sub-millimeter precision' },
      { label: 'Integration', value: 'Multi-modality imaging' }
    ],
    features: [
      'Real-time navigation',
      'Pre-operative planning',
      'Intra-operative imaging'
    ],
    inStock: true
  },
  {
    id: '24',
    name: 'Digital Fluoroscopy System',
    category: 'Imaging Equipment',
    manufacturer: 'Fluoroscopy Innovations',
    image: '/assets/images/digital_mammography_unit.png',
    alt: 'Digital fluoroscopy system for real-time imaging procedures',
    compliance: ['FDA', 'CE', 'ISO 13485'],
    description: 'Advanced digital fluoroscopy system for diagnostic and interventional procedures.',
    specifications: [
      { label: 'Detector', value: 'Flat panel digital detector' },
      { label: 'Table', value: 'Multi-position fluoroscopy table' },
      { label: 'Imaging', value: 'Pulsed fluoroscopy' }
    ],
    features: [
      'Low-dose imaging',
      'Advanced image processing',
      'DICOM compliant'
    ],
    inStock: true
  },
  {
    id: '25',
    name: 'Cone Beam CT Scanner',
    category: 'Imaging Equipment',
    manufacturer: 'Dental and Maxillofacial Imaging',
    image: '/assets/images/portable_xray_mobile_unit.png',
    alt: 'Cone beam CT scanner for dental and maxillofacial imaging',
    compliance: ['FDA', 'CE'],
    description: 'Specialized cone beam CT scanner for dental, ENT, and maxillofacial applications.',
    specifications: [
      { label: 'Field of View', value: 'Variable FOV options' },
      { label: 'Resolution', value: 'High-resolution 3D imaging' },
      { label: 'Scan Time', value: 'Fast acquisition' }
    ],
    features: [
      '3D volumetric imaging',
      'Low radiation dose',
      'Implant planning software'
    ],
    inStock: true
  },
  {
    id: '26',
    name: 'Vascular Ultrasound System',
    category: 'Imaging Equipment',
    manufacturer: 'Vascular Diagnostics Inc',
    image: '/assets/images/bone_densitometry_dexa_scanner.png',
    alt: 'Specialized vascular ultrasound system for arterial and venous imaging',
    compliance: ['FDA', 'CE'],
    description: 'Dedicated vascular ultrasound system with advanced Doppler capabilities for vascular assessment.',
    specifications: [
      { label: 'Doppler', value: 'Color and spectral Doppler' },
      { label: 'Transducers', value: 'Vascular specialty probes' },
      { label: 'Analysis', value: 'Automated vascular measurements' }
    ],
    features: [
      'High-frequency imaging',
      'Flow quantification',
      'Arterial and venous protocols'
    ],
    inStock: true
  },
  {
    id: '27',
    name: 'Radiation Therapy Planning CT',
    category: 'Imaging Equipment',
    manufacturer: 'Radiation Oncology Systems',
    image: '/assets/images/cardiac_catheterization_lab_system.png',
    alt: 'CT scanner dedicated to radiation therapy treatment planning',
    compliance: ['FDA', 'CE', 'ISO 13485'],
    description: 'Specialized CT scanner designed for radiation therapy treatment planning and simulation.',
    specifications: [
      { label: 'Aperture', value: 'Large bore for positioning' },
      { label: 'Table', value: 'Flat therapy table' },
      { label: 'Integration', value: 'RT planning software' }
    ],
    features: [
      'Treatment planning protocols',
      'Laser alignment systems',
      'Immobilization device compatible'
    ],
    inStock: true
  }];


  const filters = {
    categories: [
    { id: 'imaging', label: 'Imaging Equipment', count: 27 }],

    manufacturers: [
    { id: 'ziehm', label: 'Ziehm Imaging', count: 1 },
    { id: 'medical-imaging', label: 'Medical Imaging Solutions', count: 1 },
    { id: 'genoray', label: 'Genoray', count: 2 },
    { id: 'maven', label: 'Maven Imaging', count: 1 },
    { id: 'orthopedic', label: 'Orthopedic Imaging', count: 1 },
    { id: 'radiology-sys', label: 'Radiology Systems Inc', count: 1 },
    { id: 'med-fluoro', label: 'Medical Fluoroscopy Systems', count: 1 },
    { id: 'diagnostic', label: 'Diagnostic Imaging Corp', count: 1 },
    { id: 'ge', label: 'GE Healthcare', count: 1 },
    { id: 'siemens', label: 'Siemens Healthineers', count: 1 },
    { id: 'canon', label: 'Canon Medical Systems', count: 1 },
    { id: 'ct-imaging', label: 'CT Imaging Solutions', count: 1 },
    { id: 'mri-tech', label: 'MRI Technologies Inc', count: 1 },
    { id: 'ultrasound-diag', label: 'Ultrasound Diagnostics Corp', count: 1 },
    { id: 'womens-health', label: 'Women\'s Health Imaging', count: 1 },
    { id: 'mobile-imaging', label: 'Mobile Imaging Systems', count: 1 },
    { id: 'bone-health', label: 'Bone Health Diagnostics', count: 1 },
    { id: 'cardiac-int', label: 'Cardiac Interventions Inc', count: 1 },
    { id: 'dr-panel', label: 'DR Panel Technologies', count: 1 },
    { id: 'advanced-ir', label: 'Advanced IR Systems', count: 1 },
    { id: 'nuclear-med', label: 'Nuclear Medicine Imaging', count: 1 },
    { id: 'surgical-guid', label: 'Surgical Guidance Technologies', count: 1 },
    { id: 'fluoro-innov', label: 'Fluoroscopy Innovations', count: 1 },
    { id: 'dental-maxillo', label: 'Dental and Maxillofacial Imaging', count: 1 },
    { id: 'vascular-diag', label: 'Vascular Diagnostics Inc', count: 1 },
    { id: 'radiation-onc', label: 'Radiation Oncology Systems', count: 1 }],

    compliance: [
    { id: 'fda', label: 'FDA Approved', count: 27 },
    { id: 'ce', label: 'CE Certified', count: 24 },
    { id: 'iso', label: 'ISO 13485', count: 18 }]

  };

  const handleFilterChange = (filterType: string, value: string) => {
    setSelectedFilters((prev) => {
      const currentFilters = prev[filterType as keyof typeof prev];
      const newFilters = currentFilters.includes(value) ?
      currentFilters.filter((f) => f !== value) :
      [...currentFilters, value];

      return {
        ...prev,
        [filterType]: newFilters
      };
    });
    setCurrentPage(1);
  };

  const handleClearAllFilters = () => {
    setSelectedFilters({
      categories: [],
      manufacturers: [],
      compliance: []
    });
    setSearchQuery('');
    setCurrentPage(1);
  };

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    setCurrentPage(1);
  };

  const handleQuickView = (productId: string) => {
    const product = mockProducts.find((p) => p.id === productId);
    if (product) {
      setQuickViewProduct(product);
      setIsQuickViewOpen(true);
    }
  };

  const handleRequestQuote = (productId: string) => {
    console.log('Request quote for product:', productId);
    setIsQuickViewOpen(false);
  };

  const filteredProducts = mockProducts.filter((product) => {
    const matchesSearch =
    searchQuery === '' ||
    product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    product.manufacturer.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesCategory =
    selectedFilters.categories.length === 0 ||
    selectedFilters.categories.some((cat) =>
    product.category.toLowerCase().includes(cat.toLowerCase())
    );

    const matchesManufacturer =
    selectedFilters.manufacturers.length === 0 ||
    selectedFilters.manufacturers.some((man) =>
    product.manufacturer.toLowerCase().includes(man.toLowerCase())
    );

    const matchesCompliance =
    selectedFilters.compliance.length === 0 ||
    selectedFilters.compliance.every((comp) =>
    product.compliance.some((c) => c.toLowerCase().includes(comp.toLowerCase()))
    );

    return matchesSearch && matchesCategory && matchesManufacturer && matchesCompliance;
  });

  const sortedProducts = [...filteredProducts].sort((a, b) => {
    switch (sortBy) {
      case 'name':
        return a.name.localeCompare(b.name);
      case 'manufacturer':
        return a.manufacturer.localeCompare(b.manufacturer);
      default:
        return 0;
    }
  });

  const totalPages = Math.ceil(sortedProducts.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedProducts = sortedProducts.slice(startIndex, startIndex + itemsPerPage);

  if (!isHydrated) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="animate-pulse">
            <div className="h-12 bg-muted rounded mb-8"></div>
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
              <div className="h-96 bg-muted rounded"></div>
              <div className="lg:col-span-3">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {[...Array(6)].map((_, i) =>
                  <div key={i} className="h-96 bg-muted rounded"></div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>);

  }

  return (
    <div className="min-h-screen bg-background">
      <div className="bg-gradient-to-r from-primary to-secondary text-white py-12">
        <div className="max-w-7xl mx-auto px-4">
          <h1 className="text-4xl font-bold mb-4">Medical Equipment Catalog</h1>
          <p className="text-lg opacity-90 mb-6">
            Browse our comprehensive selection of FDA-approved medical imaging equipment
          </p>
          <SearchBar onSearch={handleSearch} suggestions={[]} />
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          <aside className="lg:col-span-1">
            <FilterSidebar
              filters={filters}
              selectedFilters={selectedFilters}
              onFilterChange={handleFilterChange}
              onClearAll={handleClearAllFilters} />

          </aside>

          <main className="lg:col-span-3">
            <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
              <div className="flex items-center gap-4">
                <p className="text-text-secondary">
                  Showing <span className="font-semibold text-text-primary">{startIndex + 1}</span> -{' '}
                  <span className="font-semibold text-text-primary">
                    {Math.min(startIndex + itemsPerPage, sortedProducts.length)}
                  </span>{' '}
                  of <span className="font-semibold text-text-primary">{sortedProducts.length}</span>{' '}
                  products
                </p>
              </div>

              <div className="flex items-center gap-3">
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-text-primary bg-card">

                  <option value="relevance">Sort by Relevance</option>
                  <option value="name">Name: A to Z</option>
                  <option value="manufacturer">Manufacturer</option>
                </select>

                <div className="flex items-center gap-1 border border-border rounded-lg p-1">
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`p-2 rounded transition-colors duration-250 ${
                    viewMode === 'grid' ? 'bg-primary text-primary-foreground' : 'text-text-secondary hover:text-text-primary'}`
                    }
                    aria-label="Grid view">

                    <Icon name="Squares2X2Icon" size={20} />
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    className={`p-2 rounded transition-colors duration-250 ${
                    viewMode === 'list' ? 'bg-primary text-primary-foreground' : 'text-text-secondary hover:text-text-primary'}`
                    }
                    aria-label="List view">

                    <Icon name="ListBulletIcon" size={20} />
                  </button>
                </div>
              </div>
            </div>

            {paginatedProducts.length === 0 ?
            <div className="text-center py-16">
                <Icon name="MagnifyingGlassIcon" size={64} className="text-text-secondary mx-auto mb-4" />
                <h3 className="text-2xl font-semibold text-text-primary mb-2">No products found</h3>
                <p className="text-text-secondary mb-6">
                  Try adjusting your filters or search query
                </p>
                <button
                onClick={handleClearAllFilters}
                className="px-6 py-3 bg-primary text-primary-foreground font-semibold rounded-lg hover:bg-secondary transition-all duration-250">

                  Clear All Filters
                </button>
              </div> :

            <>
                <div
                className={
                viewMode === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6' : 'space-y-4'
                }>

                  {paginatedProducts.map((product) =>
                <ProductCard
                  key={product.id}
                  product={product}
                  onQuickView={handleQuickView} />

                )}
                </div>

                {totalPages > 1 &&
              <div className="flex items-center justify-center gap-2 mt-8">
                    <button
                  onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="p-2 border border-border rounded-lg hover:bg-muted transition-colors duration-250 disabled:opacity-50 disabled:cursor-not-allowed"
                  aria-label="Previous page">

                      <Icon name="ChevronLeftIcon" size={20} />
                    </button>

                    {[...Array(totalPages)].map((_, index) => {
                  const pageNumber = index + 1;
                  if (
                  pageNumber === 1 ||
                  pageNumber === totalPages ||
                  pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
                  {
                    return (
                      <button
                        key={pageNumber}
                        onClick={() => setCurrentPage(pageNumber)}
                        className={`px-4 py-2 rounded-lg font-medium transition-all duration-250 ${
                        currentPage === pageNumber ?
                        'bg-primary text-primary-foreground' :
                        'border border-border hover:bg-muted text-text-primary'}`
                        }>

                            {pageNumber}
                          </button>);

                  } else if (
                  pageNumber === currentPage - 2 ||
                  pageNumber === currentPage + 2)
                  {
                    return (
                      <span key={pageNumber} className="px-2 text-text-secondary">
                            ...
                          </span>);

                  }
                  return null;
                })}

                    <button
                  onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  className="p-2 border border-border rounded-lg hover:bg-muted transition-colors duration-250 disabled:opacity-50 disabled:cursor-not-allowed"
                  aria-label="Next page">

                      <Icon name="ChevronRightIcon" size={20} />
                    </button>
                  </div>
              }
              </>
            }
          </main>
        </div>
      </div>

      <QuickViewModal
        product={quickViewProduct}
        isOpen={isQuickViewOpen}
        onClose={() => setIsQuickViewOpen(false)}
        onRequestQuote={handleRequestQuote} />

    </div>);

}